import React from "react";
import { Box, Typography, Switch } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import { COLORS } from "../assets/theme";
import CustomTable from "../components/table/CustomTable";
import SearchHeaderContainer from "../components/table/SearchHeaderContainer";
import Actions from "../components/Actions";
import GenericFormDialog, { Field } from "../components/GenericFormDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import PageHeader from "../components/PageHeader";
import CustomDialog from "../components/CustomDialog";
import CustomButton from "../components/CustomButton";
import CustomTextField from "../components/CustomTextField";

import {
  createBanner,
  deleteBanner,
  fetchBanners,
  toggleBanner,
  updateBanner,
} from "../services/BannersService";

type BannerRow = {
  id: string | number;
  title: string;
  description: string;
  priority?: number; // mapped to "order" in backend
  status: "Active" | "Inactive";
  isActive: boolean;
  imageUrl?: string;
  raw?: any;
};

const resolveImageUrl = (source: any): string | undefined => {
  if (!source) return undefined;
  return (
    source.imageUrl ||
    source.image_url ||
    source.image?.url ||
    source.image ||
    source.photo ||
    source.thumbnail ||
    source.mediaUrl
  );
};

const coerceBoolean = (value: any): boolean | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.toLowerCase().trim();
    if (["true", "1", "active", "yes"].includes(normalized)) return true;
    if (["false", "0", "inactive", "no"].includes(normalized)) return false;
  }
  return undefined;
};

const mapBannerRecord = (banner: any): BannerRow => {
  const imageUrl = resolveImageUrl(banner);

  const activeFromPayload =
    coerceBoolean(banner?.isActive) ?? coerceBoolean(banner?.is_active);

  const isActive = activeFromPayload ?? false;

  return {
    id:
      banner?.id ??
      banner?._id ??
      banner?.banner_id ??
      banner?.uuid ??
      banner?.identifier ??
      banner?.bannerId ??
      Date.now(),
    title: banner?.title ?? banner?.name ?? "",
    description: banner?.description ?? "",
    priority:
      banner?.order !== undefined
        ? Number(banner.order)
        : banner?.priority !== undefined
        ? Number(banner.priority)
        : banner?.sort_order ?? banner?.sort ?? undefined,
    status: isActive ? "Active" : "Inactive",
    isActive,
    imageUrl,
    raw: banner,
  };
};

const pickBannerList = (payload: any): any[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.banners)) return payload.banners;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.banner)) return payload.banner;
  if (payload.banner && typeof payload.banner === "object") {
    return [payload.banner];
  }
  return [];
};

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message ||
  error?.response?.data?.msg ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const showErrorToast = (message: any, opts: any = {}) =>
  toast.error(String(message), { toastId: String(message), ...opts });

const Banners: React.FC = () => {
  const [rows, setRows] = React.useState<BannerRow[]>([]);
  const [search, setSearch] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [refetchTrigger, setRefetchTrigger] = React.useState(0);

  const [editOpen, setEditOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState<BannerRow | null>(null);

  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDeleteId, setToDeleteId] = React.useState<string | number | null>(
    null
  );

  const [statusLoadingId, setStatusLoadingId] = React.useState<
    string | number | null
  >(null);

  const [imageViewOpen, setImageViewOpen] = React.useState(false);
  const [imageToViewUrl, setImageToViewUrl] = React.useState<string | null>(
    null
  );
  const [viewingImageTitle, setViewingImageTitle] = React.useState<
    string | null
  >(null);

  React.useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const res = await fetchBanners();
        const list = pickBannerList(res);
        setRows(list.map(mapBannerRecord));
      } catch (error: any) {
        console.error("Error fetching banners:", error);
        showErrorToast(getErrorMessage(error, "Failed to load banners."), {
          position: "top-right",
        });
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [refetchTrigger]);

  const openEditForId = (id: string | number) => {
    const found = rows.find((r) => String(r.id) === String(id));
    if (found) {
      setEditingRow(found);
      setEditOpen(true);
    }
  };

  const wiredActions = Actions({
    handleOpenEditModal: { handler: openEditForId },
    onDelete: {
      handler: (id: number | string) => {
        setToDeleteId(id);
        setConfirmOpen(true);
      },
    },
    onView: {
      handler: (id: number | string) => {
        const found = rows.find((r) => String(r.id) === String(id));
        if (found?.imageUrl) {
          setImageToViewUrl(found.imageUrl);
          setViewingImageTitle(found.title || "Banner Image");
          setImageViewOpen(true);
        } else {
          showErrorToast("No image available for this banner.", {
            position: "top-right",
          });
        }
      },
    },
  });

  const handleToggleStatus = async (id: string | number) => {
    setStatusLoadingId(id);

    // optimistic update
    setRows((prev) =>
      prev.map((r) =>
        String(r.id) === String(id)
          ? {
              ...r,
              isActive: !r.isActive,
              status: !r.isActive ? "Active" : "Inactive",
            }
          : r
      )
    );

    try {
      await toggleBanner(id);
      toast.success("Status updated", { position: "top-right" });
      setRefetchTrigger((prev) => prev + 1);
    } catch (error: any) {
      setRefetchTrigger((prev) => prev + 1);
      showErrorToast(getErrorMessage(error, "Failed to update status."), {
        position: "top-right",
      });
    } finally {
      setStatusLoadingId(null);
    }
  };

  const formFields: Field[] = [
    { name: "title", label: "Title", required: true },
    { name: "description", label: "Description" },
    {
      name: "priority",
      label: "Order",
      customRender: (value, onChange) => (
        <CustomTextField
          label="Order"
          value={value ?? ""}
          type="number"
          onChange={(e: any) => onChange(e.target.value)}
          fullWidth
        />
      ),
    },
    {
      name: "status",
      label: "Status",
      type: "dropdown",
      options: [
        { label: "Active", value: "Active" },
        { label: "Inactive", value: "Inactive" },
      ],
    },
    {
      name: "image",
      label: "Image",
      colSpan: 2,
      customRender: (value, onChange, helpers) => (
        <Box>
          <CustomTextField
            label="Image"
            type="file"
            highlightLabelStar
            inputProps={{ accept: "image/*" }}
            onChange={(e: any) => {
              const file = e.target.files?.[0];
              onChange(file);
              helpers?.setFieldValue("image", file);
            }}
            fullWidth
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: "block" }}
          >
            Recommended image size 405 x 150 pixels
          </Typography>

          {editingRow?.imageUrl && !value && (
            <Box sx={{ mt: 1 }}>
              <img
                src={editingRow.imageUrl}
                alt={editingRow.title}
                style={{ width: 160, height: 80, objectFit: "cover" }}
              />
            </Box>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <PageHeader
        title="Banners"
        subtitle="Promotional banners shown to your clients."
        sx={{ mt: { xs: 2, sm: 3 } }}
      />

      <SearchHeaderContainer
        search={search}
        setSearch={setSearch}
        buttonConfig={[
          {
            size: "small",
            bgColor: COLORS.MainColor,
            startIcon: <AddIcon />,
            onClick: () => {
              setEditingRow(null);
              setEditOpen(true);
            },
            textColor: COLORS.WhiteText,
          },
        ]}
      />

      <CustomTable<BannerRow>
        filters={{ page: 0, size: 10, search }}
        clientSidePagination
        loading={loading}
        fields={[
          {
            key: "imageUrl",
            header: "Image",
            transform: (r) =>
              r.imageUrl ? (
                <img
                  src={r.imageUrl}
                  alt={r.title}
                  style={{ width: 120, height: 60, objectFit: "cover" }}
                />
              ) : (
                "-"
              ),
          },
          { key: "title", header: "Title" },
          { key: "description", header: "Description" },
          {
            key: "priority",
            header: "Order",
            transform: (r) =>
              r.priority !== undefined && r.priority !== null
                ? Number(r.priority)
                : "-",
          },
          {
            key: "status",
            header: "Status",
            transform: (r) => (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Switch
                  checked={r.isActive}
                  onChange={() => handleToggleStatus(r.id)}
                  disabled={statusLoadingId === r.id}
                />
                <Typography sx={{ ml: 1 }}>{r.status}</Typography>
              </Box>
            ),
          },
        ]}
        actions={wiredActions}
        fetchFunc={async () => ({
          content: rows,
          number: 0,
          size: rows.length,
          totalElements: rows.length,
          totalPages: 1,
        })}
      />

      <GenericFormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={editingRow ? "Edit Banner" : "Add Banner"}
        fields={formFields}
        initialValues={
          editingRow
            ? {
                title: editingRow.title,
                description: editingRow.description,
                priority: editingRow.priority?.toString() ?? "",
                status: editingRow.isActive ? "Active" : "Inactive",
                image: "",
              }
            : { status: "Inactive" }
        }
        onSubmit={async (values) => {
          try {
            const payload = {
              title: values.title?.trim() || "",
              description: values.description ?? "",
              order:
                values.priority !== undefined &&
                values.priority !== "" &&
                !isNaN(Number(values.priority)) &&
                Number(values.priority) >= 0
                  ? Math.floor(Number(values.priority))
                  : 0,
              isActive: (values.status || "Inactive") === "Active",
            };

            const file =
              values.image && typeof values.image === "object"
                ? (values.image as File | Blob)
                : undefined;

            const hasExistingImage = Boolean(editingRow?.imageUrl);

            if (!file && !hasExistingImage) {
              showErrorToast("Image is required.", { position: "top-right" });
              return;
            }

            if (editingRow) {
              await updateBanner(editingRow.id, payload, file);
              toast.success("Banner updated successfully!");
            } else {
              await createBanner(payload, file);
              toast.success("Banner created successfully!");
            }

            setEditOpen(false);
            setEditingRow(null);
            setRefetchTrigger((prev) => prev + 1);
          } catch (error: any) {
            showErrorToast(getErrorMessage(error, "Failed to save banner."));
          }
        }}
      />

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Delete entry"
        message="Are you sure you want to delete this entry?"
        onConfirm={async () => {
          if (!toDeleteId) return;
          try {
            await deleteBanner(toDeleteId);
            setRefetchTrigger((prev) => prev + 1);
            toast.success("Banner deleted successfully!");
          } catch (error: any) {
            showErrorToast(getErrorMessage(error, "Failed to delete banner."));
          } finally {
            setConfirmOpen(false);
            setToDeleteId(null);
          }
        }}
      />

      <CustomDialog
        open={imageViewOpen}
        onClose={() => setImageViewOpen(false)}
        title={viewingImageTitle || "Image"}
        actions={
          <CustomButton
            label="Close"
            onClick={() => setImageViewOpen(false)}
            bgColor={COLORS.MainColor}
            textColor={COLORS.WhiteText}
          />
        }
      >
        {imageToViewUrl ? (
          <img
            src={imageToViewUrl}
            alt="banner"
            style={{ maxWidth: "100%", maxHeight: "70vh" }}
          />
        ) : (
          <Typography>No image to display</Typography>
        )}
      </CustomDialog>
    </Box>
  );
};

export default Banners;
